import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Home, DoorOpen, ThermometerSun, ReceiptText, ShoppingBag,
  Menu, Users, CalendarCheck, FileText, FileBarChart, Zap, Package2,
  Warehouse, Beef, Droplets, HeartPulse, Bird, Baby, Dna, Handshake,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useFarmModules } from '../../context/FarmModuleContext';
import { canAccessFinancialModules } from '../../utils/roles';

function hasFeatureAccess(limit) {
  return limit !== 0;
}

export default function BottomNav() {
  const { t } = useTranslation();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, planLimits } = useAuth();
  const { hasPoultry, hasDairy, viewModule, setViewModule } = useFarmModules();
  const canViewFinancial = canAccessFinancialModules(user?.role);

  // Show switcher only when the user has both modules active
  const isMultiModule = hasPoultry && hasDairy;

  // Auto-switch tab when the user navigates between module routes.
  // viewModule is shared (FarmModuleContext) so this stays in sync with the
  // Dashboard's own Poultry/Dairy tab instead of drifting independently.
  useEffect(() => {
    if (location.pathname.startsWith('/dairy') && hasDairy) {
      setViewModule('DAIRY');
    } else if (!location.pathname.startsWith('/dairy') && hasPoultry) {
      setViewModule('POULTRY');
    }
  }, [location.pathname, hasDairy, hasPoultry, setViewModule]);

  const limits = {
    employees: planLimits?.employees ?? 0,
    attendance: planLimits?.attendance ?? 0,
    payroll: planLimits?.payroll ?? 0,
    reports: planLimits?.reports ?? 0,
    inventory: planLimits?.inventory ?? 0,
  };

  // ── Nav items per module ────────────────────────────────────────────────────
  const POULTRY_NAV = [
    { to: '/', label: t('navigation.home'), Icon: Home, end: true },
    { to: '/rooms', label: t('navigation.rooms'), Icon: DoorOpen },
    { to: '/incubator', label: t('navigation.incubator'), Icon: ThermometerSun },
  ];

  const DAIRY_NAV = [
    { to: '/', label: t('navigation.home'), Icon: Home, end: true },
    { to: '/dairy/sheds', label: t('navigation.dairySheds', 'Sheds'), Icon: Warehouse },
    { to: '/dairy/animals', label: t('navigation.dairyAnimals', 'Animals'), Icon: Beef },
    { to: '/dairy/milk', label: t('navigation.dairyMilk', 'Milk'), Icon: Droplets },
  ];

  const FINANCIAL_MORE = [
    { to: '/inventory', label: t('navigation.inventory'), Icon: Package2, financial: true, limitKey: 'inventory' },
    { to: '/expenses', label: t('navigation.expenses'), Icon: ReceiptText, financial: true },
    { to: '/sales', label: t('navigation.sales'), Icon: ShoppingBag, financial: true },
    { to: '/employees', label: t('navigation.employees'), Icon: Users, financial: true, limitKey: 'employees' },
    { to: '/attendance', label: t('navigation.attendance'), Icon: CalendarCheck, financial: true, limitKey: 'attendance' },
    { to: '/payroll', label: t('navigation.payroll'), Icon: FileText, financial: true, limitKey: 'payroll' },
    { to: '/reports', label: t('navigation.reports'), Icon: FileBarChart, financial: true, limitKey: 'reports' },
  ];

  // Dairy puts Health + Breeding in More since the bottom bar holds only 4 items
  const DAIRY_MORE = [
    { to: '/dairy/trading',  label: t('navigation.dairyTrading',  'Milk Trading'), Icon: Handshake, financial: true },
    { to: '/dairy/health',   label: t('navigation.dairyHealth',   'Health'),   Icon: HeartPulse },
    { to: '/dairy/breeding', label: t('navigation.dairyBreeding', 'Breeding'), Icon: Dna },
    ...FINANCIAL_MORE,
  ];

  const currentNav = viewModule === 'DAIRY' && hasDairy ? DAIRY_NAV : POULTRY_NAV;
  const currentMore = viewModule === 'DAIRY' && hasDairy ? DAIRY_MORE : FINANCIAL_MORE;

  const navItems = currentNav.filter((item) => !item.financial || canViewFinancial);
  const moreItems = currentMore.filter((item) => !item.financial || canViewFinancial);

  const handleMoreItemClick = (item) => {
    const limit = item.limitKey ? limits[item.limitKey] : null;
    const blocked = limit !== null && !hasFeatureAccess(limit);
    navigate(blocked ? '/upgrade' : item.to);
    setMoreOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) {
        setMoreOpen(false);
      }
    };
    if (moreOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [moreOpen]);

  // Active colour changes per module
  const activeColor = viewModule === 'DAIRY'
    ? 'text-blue-600 dark:text-blue-400'
    : 'text-primary-800 dark:text-primary-400';
  const activeBg = viewModule === 'DAIRY'
    ? 'bg-blue-50 dark:bg-blue-900/30'
    : 'bg-primary-50 dark:bg-primary-900/30';

  return (
    <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-20 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 safe-bottom">
      <div ref={moreRef} className="relative">

        {/* ── More menu popup ─────────────────────────────────────────────────── */}
        {moreOpen && moreItems.length > 0 && (
          <div className="absolute bottom-full left-0 right-0 mb-2 px-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="grid grid-cols-4 gap-3">
                {moreItems.map((item) => {
                  const { to, label, Icon, limitKey } = item;
                  const limit = limitKey ? limits[limitKey] : null;
                  const noAccess = limit !== null && !hasFeatureAccess(limit);
                  return (
                    <button
                      key={to}
                      onClick={() => handleMoreItemClick(item)}
                      className={`flex flex-col items-center justify-center p-3 rounded-lg transition-colors relative ${
                        noAccess
                          ? 'text-gray-400 dark:text-gray-500 opacity-60'
                          : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <div className="relative">
                        <Icon size={22} className="mb-1" />
                        {noAccess && (
                          <span className="absolute -top-1 -right-1 w-3 h-3 bg-amber-500 rounded-full flex items-center justify-center">
                            <Zap size={7} className="text-white" />
                          </span>
                        )}
                      </div>
                      <span className={`text-xs font-medium ${noAccess ? 'line-through decoration-gray-400/30' : ''}`}>
                        {label}
                      </span>
                      {noAccess && (
                        <span className="absolute top-1 right-1 px-1 py-0.5 rounded bg-amber-500/20 text-amber-600 dark:text-amber-400 text-[7px] font-bold uppercase">
                          Pro
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── Module switcher — only when both Poultry + Dairy are active ─────── */}
        {isMultiModule && (
          <div className="flex border-b border-gray-100 dark:border-gray-800">
            <button
              onClick={() => { setViewModule('POULTRY'); setMoreOpen(false); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-semibold transition-colors ${
                viewModule === 'POULTRY'
                  ? 'text-primary-700 dark:text-primary-400 border-b-2 border-primary-600 dark:border-primary-400'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              <Bird size={13} />
              {t('navigation.poultry', 'Poultry')}
            </button>
            <button
              onClick={() => { setViewModule('DAIRY'); setMoreOpen(false); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-semibold transition-colors ${
                viewModule === 'DAIRY'
                  ? 'text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400'
                  : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              <Beef size={13} />
              {t('navigation.dairy', 'Dairy')}
            </button>
          </div>
        )}

        {/* ── Bottom nav items ────────────────────────────────────────────────── */}
        <div className="flex">
          {navItems.map(({ to, label, Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[10px] font-medium transition-colors ${
                  isActive ? activeColor : 'text-gray-400 dark:text-gray-500'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isActive ? activeBg : ''}`}>
                    <Icon size={20} />
                  </span>
                  {label}
                </>
              )}
            </NavLink>
          ))}

          {/* More button */}
          {moreItems.length > 0 && (
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className={`flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[10px] font-medium transition-colors ${
                moreOpen ? activeColor : 'text-gray-400 dark:text-gray-500'
              }`}
            >
              <span className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${moreOpen ? activeBg : ''}`}>
                <Menu size={20} />
              </span>
              {t('navigation.more')}
            </button>
          )}
        </div>
      </div>
    </nav>
  );
}
